import { useState, type ReactNode } from 'react';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { moveFolderApi } from '@/services/api/folders';
import { useMoveMail } from '@/hooks/useMailMutations';
import { useFileMailToasts } from '@/features/multi-select/useFileMailToasts';
import { useBatchActions } from '@/features/multi-select/useBatchActions';
import { summarizeBatch } from '@/features/multi-select/selection';
import { useNotify } from '@/features/notifications/useNotify';
import { useItemSelectionStore } from '@/stores/itemSelectionStore';
import { addBreadcrumb } from '@/services/breadcrumbs/breadcrumbs';
import { reportError } from '@/services/errors/errorReporter';
import { FolderItemIcon, MailItemIcon } from '@/components/ItemIcon';
import { tokens } from '@fluentui/react-components';

/**
 * Ce qu'un élément traîné transporte. Contrat PARTAGÉ : `DraggableFolder`
 * (arbre), les lignes de la page détail et les règles de dépôt ci-dessous s'y
 * réfèrent toutes — c'est ce qui permet aux deux écrans d'obéir aux mêmes règles
 * sans les réécrire.
 *
 * `folderId` d'un mail est nullable : un mail rangé à la RACINE d'un projet n'a
 * pas de dossier, cas que la page détail affiche (l'arbre, lui, ne montre les
 * mails que sous un dossier).
 */
export type DragData =
  | { type: 'folder'; folderId: string; containerId: string; parentId: string | null }
  | { type: 'mail'; mailId: string; folderId: string | null; containerId: string };

/**
 * Règles de glisser-déposer des ÉLÉMENTS (dossiers et mails), communes à l'arbre
 * de la page d'accueil et à la page détail.
 *
 * Vivait dans `features/tree` sous le nom `TreeDndContext` : rien dedans n'était
 * propre à l'arbre, et la page détail avait besoin exactement des mêmes règles —
 * les dupliquer aurait fait dériver deux jeux de règles pour un même geste, avec
 * le risque qu'un déplacement soit permis d'un côté et refusé de l'autre.
 */
export function ItemDndContext({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  // 6px activation distance so a normal click on a folder row does not
  // accidentally start a drag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );
  const [activeDrag, setActiveDrag] = useState<DragData | null>(null);
  // Nombre d'éléments effectivement traînés : 1, ou la taille de la
  // multi-sélection quand l'élément saisi en fait partie. Sert à l'étiquette qui
  // suit le curseur — traîner quatre lignes en n'en voyant qu'une décrite est le
  // genre de détail qui fait relâcher au mauvais endroit.
  const [dragCount, setDragCount] = useState(1);
  const moveMailMut = useMoveMail();
  const { showError } = useFileMailToasts();
  const { move: batchMove } = useBatchActions();
  const { notify } = useNotify();

  const move = useMutation({
    mutationFn: moveFolderApi,
    onSettled: (_data, _err, args) => {
      qc.invalidateQueries({ queryKey: ['folders'] });
      if (args.newContainerId) {
        qc.invalidateQueries({ queryKey: ['folders', args.newContainerId] });
      }
    },
    onError: (err) => {
      reportError({
        error: err instanceof Error ? err : new Error(String(err)),
        tags: ['dnd', 'move-folder'],
      });
    },
  });

  /** L'identifiant de l'élément saisi, dans le vocabulaire de la sélection. */
  const idOf = (d: DragData): string => (d.type === 'mail' ? d.mailId : d.folderId);

  /**
   * La multi-sélection à emporter avec ce glisser, ou `null`.
   *
   * RÈGLE DE L'EXPLORATEUR : traîner un élément DE la sélection emporte toute la
   * sélection ; traîner un élément qui n'en fait pas partie l'abandonne et ne
   * déplace que lui. Sans la seconde moitié, on déplacerait des éléments que
   * l'utilisateur ne regarde même plus.
   */
  const selectionForDrag = (data: DragData) => {
    const { items } = useItemSelectionStore.getState();
    if (items.length > 1 && items.some((i) => i.id === idOf(data))) return items;
    return null;
  };

  const onDragStart = (e: DragStartEvent) => {
    const data = e.active.data.current as DragData | undefined;
    if (!data) return;
    setActiveDrag(data);
    setDragCount(selectionForDrag(data)?.length ?? 1);
  };

  const onDragEnd = (e: DragEndEvent) => {
    setActiveDrag(null);
    setDragCount(1);
    const active = e.active.data.current as DragData | undefined;
    const over = e.over?.data.current as DragData | undefined;
    if (!active || !over) {
      addBreadcrumb({
        category: 'mutation',
        message: 'dnd dropped outside',
        data: { hadOver: Boolean(over) },
      });
      return;
    }
    // Both mails and folders drop onto folder rows only.
    if (over.type !== 'folder') return;

    // DÉPÔT D'UN LOT. Passe par le même chemin que « Déplacer vers… » du menu
    // contextuel : élagage des éléments emportés par un dossier du lot, issues
    // par élément, un seul compte rendu. Le chemin unitaire ci-dessous reste
    // inchangé — il porte le cas de très loin le plus fréquent.
    const batch = selectionForDrag(active);
    if (batch) {
      addBreadcrumb({
        category: 'mutation',
        message: 'batch-move dnd',
        data: { count: batch.length, targetFolderId: over.folderId },
      });
      void batchMove({
        refs: batch,
        targetFolderId: over.folderId,
        targetContainerId: over.containerId,
      }).then(({ outcomes, carried }) => {
        const failed = outcomes.filter((o) => o.status === 'error').length;
        const done = outcomes.filter((o) => o.status === 'done').length;
        notify(
          failed > 0 ? (done > 0 ? 'warning' : 'error') : 'success',
          failed > 0 ? 'Déplacement partiel' : 'Déplacé',
          summarizeBatch(outcomes, 'déplacé', carried),
        );
        useItemSelectionStore.getState().clear();
      });
      return;
    }

    // Un glisser hors sélection l'abandonne (cf. selectionForDrag).
    if (useItemSelectionStore.getState().items.length > 0) {
      useItemSelectionStore.getState().clear();
    }

    // Dragging a filed mail onto a folder → move it there.
    if (active.type === 'mail') {
      if (active.folderId === over.folderId) return;
      addBreadcrumb({
        category: 'mutation',
        message: 'move-mail dnd',
        data: { mailId: active.mailId, targetFolderId: over.folderId },
      });
      moveMailMut.mutate(
        {
          mailId: active.mailId,
          sourceFolderId: active.folderId,
          targetFolderId: over.folderId,
        },
        {
          // Don't let a failed drag-move (e.g. a duplicate in the target) fail
          // silently — the mail visibly stays put otherwise.
          onError: (err) => showError(err instanceof Error ? err.message : String(err)),
        },
      );
      return;
    }

    // Dragging a folder onto another folder → reparent.
    if (active.folderId === over.folderId) return;
    if (active.parentId === over.folderId) return;

    addBreadcrumb({
      category: 'mutation',
      message: 'move-folder dnd',
      data: { folderId: active.folderId, newParentId: over.folderId },
    });

    move.mutate(
      {
        folderId: active.folderId,
        newParentId: over.folderId,
        newContainerId: over.containerId !== active.containerId ? over.containerId : undefined,
      },
      {
        // Un déplacement refusé doit se DIRE, comme pour les mails juste au-dessus.
        // Le cas courant n'est pas théorique : déposer un dossier dans l'un de ses
        // propres descendants créerait un cycle, et c'est le serveur qui l'arrête
        // (parcours des `parent_id`) — sans ce toast, le dossier revenait
        // simplement à sa place sans un mot, ce qui se lit comme un geste raté
        // plutôt que comme un refus.
        onError: (err) => showError(err instanceof Error ? err.message : String(err)),
      },
    );
  };

  return (
    <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
      {children}
      <DragOverlay dropAnimation={null}>
        {activeDrag ? (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 8px',
              borderRadius: 4,
              backgroundColor: tokens.colorBrandBackground2,
              color: tokens.colorBrandForeground1,
              boxShadow: tokens.shadow8,
              fontSize: tokens.fontSizeBase200,
              fontWeight: tokens.fontWeightSemibold,
              cursor: 'grabbing',
            }}
          >
            {dragCount > 1 ? (
              <>
                {activeDrag.type === 'mail' ? <MailItemIcon /> : <FolderItemIcon />}
                {dragCount} éléments en déplacement
              </>
            ) : activeDrag.type === 'mail' ? (
              <>
                <MailItemIcon />
                Mail en déplacement
              </>
            ) : (
              <>
                <FolderItemIcon />
                Dossier en déplacement
              </>
            )}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
